<div class="main-view space-y-2 md:space-y-4">
    <div class="flex flex-wrap items-start gap-2">
        <app-page-surface-header titleIcon="clients" class="min-w-0 flex-1" title="Add client"
            subtitle="Onboard a new client with tax details and commercial profile." />
    </div>

    <form (ngSubmit)="onSubmit()" class="space-y-6">
        <div class="rounded-3xl bg-white/60 p-6 shadow-sm dark:bg-[#0e1726] md:p-8">
            <!-- Basic Details -->
            <div class="space-y-1">
                <h2 class="text-lg font-bold text-black dark:text-white">Basic Details</h2>
                <p class="text-sm text-gray-500 dark:text-gray-400">Provide your business and contact information
                    accurately.</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                    <span class="font-semibold text-red-600">*</span> Required field
                </p>
            </div>

            <div class="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
                <div>
                    <label for="businessName" class="mb-1.5 block text-sm font-medium text-black dark:text-white">
                        Business Name <span class="font-semibold text-red-600" aria-hidden="true">*</span>
                    </label>
                    <input id="businessName" name="businessName" type="text" [(ngModel)]="businessName"
                        (ngModelChange)="clearFieldFeedback('businessName')" (blur)="maybeSuggestSubdomainFromName()"
                        class="form-input w-full rounded-lg" placeholder="Legal / display name" />
                    <p *ngIf="fieldErrors.businessName" class="mt-1 text-xs text-red-600 dark:text-red-400"
                        role="alert">{{ fieldErrors.businessName }}</p>
                </div>
                <div class="md:col-span-1">
                    <label for="tenantSubdomain"
                        class="mb-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm font-medium text-black dark:text-white">
                        <span>Client subdomain <span class="font-semibold text-red-600"
                                aria-hidden="true">*</span></span>
                        <span class="text-xs font-normal text-gray-500 dark:text-gray-400">3–63 chars, lowercase,
                            hyphens</span>
                    </label>
                    <div class="flex gap-2">
                        <input id="tenantSubdomain" name="tenantSubdomain" type="text" [(ngModel)]="tenantSubdomain"
                            (ngModelChange)="clearFieldFeedback('tenantSubdomain')" autocomplete="off"
                            class="form-input min-w-0 flex-1 rounded-lg lowercase" placeholder="e.g. acme-finance"
                            maxlength="63" />
                        <button type="button"
                            class="btn shrink-0 rounded-lg bg-[#FFD85F] px-3 text-xs font-semibold text-black hover:opacity-90"
                            (click)="suggestSubdomain()">
                            Suggest
                        </button>
                    </div>
                    <p *ngIf="fieldErrors.tenantSubdomain" class="mt-1 text-xs text-red-600 dark:text-red-400"
                        role="alert">{{ fieldErrors.tenantSubdomain }}</p>
                </div>
                <div class="md:col-span-1">
                    <app-intl-phone-field #contactPhoneField inputId="contactNumber" name="contactNumber"
                        label="Contact Number" placeholder="Phone number" [(countryIso)]="contactCountryIso"
                        [(nationalNumber)]="contactNational" (nationalNumberChange)="onContactPhoneEdited()"
                        (countryIsoChange)="onContactPhoneEdited()" />
                    <p *ngIf="fieldErrors.contactPhone" class="mt-1 text-xs text-red-600 dark:text-red-400"
                        role="alert">{{ fieldErrors.contactPhone }}</p>
                </div>
                <div>
                    <label for="contactPerson" class="mb-1.5 block text-sm font-medium">Contact Person</label>
                    <input id="contactPerson" name="contactPerson" type="text" [(ngModel)]="contactPerson"
                        (ngModelChange)="clearSubmitErrorOnly()" class="form-input w-full rounded-lg"
                        placeholder="Enter Name" />
                </div>

                <div class="md:col-span-1">
                    <app-intl-phone-field #personPhoneField inputId="contactPersonPhone" name="contactPersonPhone"
                        label="Contact Person Phone Number" placeholder="Phone number"
                        [(countryIso)]="contactPersonCountryIso" [(nationalNumber)]="contactPersonNational"
                        (nationalNumberChange)="onPersonPhoneEdited()" (countryIsoChange)="onPersonPhoneEdited()" />
                    <p *ngIf="fieldErrors.contactPersonPhone" class="mt-1 text-xs text-red-600 dark:text-red-400"
                        role="alert">{{ fieldErrors.contactPersonPhone }}</p>
                </div>
                <!-- Full-width inner grid so Email/GST/PAN rows align to the same 3 columns (parent grid auto-flow was putting Email in column 3). -->
                <div class="w-full min-w-0 md:col-span-3">
                    <div class="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5 md:gap-y-6">
                        <div class="min-w-0">
                            <label for="email" class="mb-1.5 block text-sm font-medium text-black dark:text-white">
                                Email <span class="font-semibold text-red-600" aria-hidden="true">*</span>
                            </label>
                            <input id="email" name="email" type="email" [(ngModel)]="email"
                                (ngModelChange)="clearFieldFeedback('email')" class="form-input w-full rounded-lg"
                                placeholder="Enter Email" />
                            <p *ngIf="fieldErrors.email" class="mt-1 text-xs text-red-600 dark:text-red-400"
                                role="alert">{{ fieldErrors.email }}</p>
                        </div>
                        <div class="min-w-0">
                            <label for="country_iso2"
                                class="mb-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm font-medium text-black dark:text-white">
                                <span>Country <span class="font-semibold text-red-600"
                                        aria-hidden="true">*</span></span>
                                <span class="text-xs font-normal text-gray-500 dark:text-gray-400">Sets the rule
                                    pack, currency, timezone and required tax IDs</span>
                            </label>
                            <select id="country_iso2" name="country_iso2" [(ngModel)]="countryIso2"
                                class="form-select w-full rounded-lg">
                                <option *ngFor="let c of countryOptions" [value]="c.iso2">{{ c.label }}</option>
                            </select>
                        </div>

                        <!-- UAE collects a TRN instead of GSTIN/PAN. -->
                        <div class="min-w-0" *ngIf="isUaeTenant">
                            <label for="trn"
                                class="mb-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm font-medium text-black dark:text-white">
                                <span>Tax Registration Number (TRN) <span class="font-semibold text-red-600"
                                        aria-hidden="true">*</span></span>
                                <span class="text-xs font-normal text-gray-500 dark:text-gray-400">Required ·
                                    15 digits</span>
                            </label>
                            <input id="trn" name="trn" type="text" [(ngModel)]="trn"
                                (ngModelChange)="clearFieldFeedback('trn')" maxlength="15"
                                class="form-input w-full rounded-lg" placeholder="15-digit TRN" autocomplete="off" />
                            <p *ngIf="fieldErrors.trn" class="mt-1 text-xs text-red-600 dark:text-red-400"
                                role="alert">{{ fieldErrors.trn }}</p>
                        </div>

                        <div class="min-w-0" *ngIf="isIndiaTenant">
                            <label for="gstn"
                                class="mb-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm font-medium text-black dark:text-white">
                                <span>GST number (GSTIN) <span class="font-semibold text-red-600"
                                        aria-hidden="true">*</span></span>
                                <span class="text-xs font-normal text-gray-500 dark:text-gray-400">Required ·
                                    15-character Indian GSTIN</span>
                            </label>
                            <input id="gstn" name="gstn" type="text" [(ngModel)]="gstn"
                                (ngModelChange)="clearFieldFeedback('gstn')" (blur)="blurNormalizeGst()" maxlength="15"
                                class="form-input w-full rounded-lg uppercase" placeholder="15-character GSTIN"
                                autocomplete="off" />
                            <p *ngIf="fieldErrors.gstn" class="mt-1 text-xs text-red-600 dark:text-red-400"
                                role="alert">{{ fieldErrors.gstn }}</p>
                        </div>
                        <div class="min-w-0">
                            <label for="billingAddress"
                                class="mb-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm font-medium text-black dark:text-white">
                                <span>Billing address</span>
                                <span class="text-xs font-normal text-gray-500 dark:text-gray-400">Optional · printed on invoices</span>
                            </label>
                            <input id="billingAddress" name="billingAddress" type="text" [(ngModel)]="billingAddress"
                                maxlength="255" class="form-input w-full rounded-lg" autocomplete="off" />
                        </div>
                        <div class="min-w-0">
                            <label for="billingCity" class="mb-1.5 text-sm font-medium text-black dark:text-white">City</label>
                            <input id="billingCity" name="billingCity" type="text" [(ngModel)]="billingCity"
                                maxlength="100" class="form-input w-full rounded-lg" autocomplete="off" />
                        </div>
                        <div class="min-w-0">
                            <label for="billingPincode" class="mb-1.5 text-sm font-medium text-black dark:text-white">Pincode</label>
                            <input id="billingPincode" name="billingPincode" type="text" [(ngModel)]="billingPincode"
                                maxlength="12" class="form-input w-full rounded-lg" autocomplete="off" />
                        </div>
                        <div class="min-w-0">
                            <div class="mb-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                                <span class="text-sm font-medium text-black dark:text-white">Company logo</span>
                                <span class="text-xs font-normal text-gray-500 dark:text-gray-400">Optional · client
                                    brand image</span>
                            </div>
                            <div
                                class="flex min-h-[38px] min-w-0 flex-wrap items-center gap-3 rounded-lg border border-white-light bg-white px-2 py-1 dark:border-[#191e3a] dark:bg-dark">
                                <label
                                    class="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-[#FFD85F] px-4 py-1 mb-0 text-xs font-semibold text-black transition hover:opacity-90">
                                    <icon-folder-plus class="size-4 shrink-0" />
                                    Choose logo
                                    <input type="file" accept="image/*" class="sr-only"
                                        (change)="onLogoSelected($event)" />
                                </label>
                                <span class="min-w-0 truncate text-sm text-gray-500 dark:text-gray-400">{{ logoFileName
                                    || 'No file chosen' }}</span>
                            </div>
                        </div>

                        <div class="min-w-0 md:self-start">
                            <label for="businessPan"
                                class="mb-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm font-medium text-black dark:text-white">
                                <span>Business PAN <span class="font-semibold text-red-600"
                                        aria-hidden="true">*</span></span>
                                <span class="text-xs font-normal text-gray-500 dark:text-gray-400">Required · format
                                    ABCDE1234F</span>
                            </label>
                            <input id="businessPan" name="businessPan" type="text" [(ngModel)]="businessPan"
                                (ngModelChange)="clearFieldFeedback('businessPan')" (blur)="blurNormalizePan()"
                                maxlength="10" class="form-input w-full rounded-lg uppercase" placeholder="ABCDE1234F"
                                autocomplete="off" />
                            <p *ngIf="fieldErrors.businessPan" class="mt-1 text-xs text-red-600 dark:text-red-400"
                                role="alert">{{ fieldErrors.businessPan }}</p>
                        </div>
                        <div class="min-w-0 md:col-span-2 md:self-start">
                            <div class="mb-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                                <span class="text-sm font-medium text-black dark:text-white">
                                    PAN card image <span class="font-semibold text-red-600" aria-hidden="true">*</span>
                                </span>
                                <span class="text-xs font-normal text-gray-500 dark:text-gray-400">Scan or photo · same
                                    PAN as left</span>
                            </div>
                            <div
                                class="flex min-h-[38px] min-w-0 flex-wrap items-center gap-3 rounded-lg border border-white-light bg-white px-2 py-1 dark:border-[#191e3a] dark:bg-dark">
                                <label
                                    class="inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-lg bg-[#FFD85F] px-4 py-1 mb-0 text-xs font-semibold text-black transition hover:opacity-90">
                                    <icon-folder-plus class="size-4 shrink-0" />
                                    Choose PAN image
                                    <input type="file" accept="image/*" class="sr-only"
                                        (change)="onProfilePictureSelected($event)" />
                                </label>
                                <span class="min-w-0 flex-1 truncate text-sm text-gray-500 dark:text-gray-400">{{
                                    profilePictureFileName || 'No file chosen' }}</span>
                            </div>
                            <p *ngIf="fieldErrors.panImage" class="mt-1 text-xs text-red-600 dark:text-red-400"
                                role="alert">{{ fieldErrors.panImage }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div class="my-6 border-t border-dashed border-gray-300 dark:border-[#191e3a]"></div>

            <!-- Product types -->
            <div class="space-y-1">
                <h2 class="text-lg font-bold text-black dark:text-white">Product types</h2>
                <p class="text-sm text-gray-500 dark:text-gray-400">Select product types from the catalog (loaded from the API).</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                    <span class="font-semibold text-red-600">*</span> At least one product type required
                </p>
            </div>

            <div class="mt-6 space-y-6">
                <p *ngIf="catalogLoading" class="text-sm text-gray-500 dark:text-gray-400">
                    Loading product types…
                </p>
                <p *ngIf="!catalogLoading && catalogLoadError"
                    class="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
                    {{ catalogLoadError }}
                </p>

                <div *ngIf="!catalogLoading && !catalogLoadError">
                    <div>
                        <div class="mb-3 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            <span class="text-sm font-medium text-black dark:text-white">
                                Product type <span class="font-semibold text-red-600" aria-hidden="true">*</span>
                            </span>
                            <span class="text-xs font-normal text-gray-500 dark:text-gray-400">Pick at least one
                                type.</span>
                        </div>
                        <p *ngIf="fieldErrors.products" class="mb-2 text-xs text-red-600 dark:text-red-400"
                            role="alert">{{ fieldErrors.products }}</p>
                        <div class="flex flex-wrap gap-4">
                            <label *ngFor="let row of productsCatalog; trackBy: trackBySlug"
                                class="inline-flex cursor-pointer items-center gap-2 rounded-full border border-white-light bg-white px-4 py-2 text-sm font-medium dark:border-[#191e3a] dark:bg-dark">
                                <input type="checkbox" class="form-checkbox" [name]="'product-' + row.slug"
                                    [(ngModel)]="productSelection[row.slug]"
                                    (ngModelChange)="onProductCheckboxChange()" />
                                {{ row.name }}
                            </label>
                        </div>

                        <!-- Billing: type drives whether per-PD rates and frequency apply. -->
                        <div class="mb-6 mt-4">
                            <h3 class="mb-3 text-base font-semibold">Billing</h3>

                            <fieldset class="mb-4 min-w-0 border-0 p-0">
                                <legend class="mb-1.5 block text-sm font-medium">Billing Type <span
                                        class="text-danger">*</span></legend>
                                <div
                                    class="inline-flex overflow-hidden rounded-md border border-gray-300 dark:border-gray-700">
                                    <button type="button" (click)="onBillingTypeChange('PREPAID')"
                                        [class]="!isPostpaid ? 'bg-primary px-6 py-2 text-sm text-white' : 'px-6 py-2 text-sm'"
                                        [attr.aria-pressed]="!isPostpaid">
                                        Pre-Paid
                                    </button>
                                    <button type="button" (click)="onBillingTypeChange('POSTPAID')"
                                        [class]="isPostpaid ? 'bg-primary px-6 py-2 text-sm text-white' : 'px-6 py-2 text-sm'"
                                        [attr.aria-pressed]="isPostpaid">
                                        Post-Paid
                                    </button>
                                </div>
                            </fieldset>

                            <div *ngIf="isPostpaid">
                                <label for="billing-frequency" class="mb-1.5 block text-sm font-medium">
                                    Frequency <span class="text-danger">*</span>
                                </label>
                                <select id="billing-frequency" name="billing-frequency" class="form-select"
                                    [(ngModel)]="billingFrequency" (ngModelChange)="clearSubmitErrorOnly()">
                                    <option value="" disabled>Select frequency</option>
                                    <option *ngFor="let f of billingFrequencies" [value]="f.value">{{ f.label }}
                                    </option>
                                </select>
                            </div>

                            <p *ngIf="!isPostpaid" class="text-sm text-gray-500 dark:text-gray-400">
                                Pre-paid tenants are billed through subscription plans, not per PD.
                            </p>
                        </div>

                        <!-- Per-PD charge for each selected product type -->
                        <ng-container *ngFor="let row of productsCatalog; trackBy: trackBySlug">
                            <div *ngIf="productSelection[row.slug] && isPostpaid" class="mt-3">
                                <label [for]="'charge-' + row.slug" class="mb-1.5 block text-sm font-medium">
                                    {{ row.name }} — charge per PD ({{ currencySymbol }})
                                </label>
                                <input [id]="'charge-' + row.slug" [name]="'charge-' + row.slug" type="text"
                                    inputmode="decimal" class="form-input w-full rounded-lg"
                                    [(ngModel)]="productCharges[row.slug]" (ngModelChange)="clearSubmitErrorOnly()"
                                    placeholder="e.g. 50.00" />
                            </div>
                        </ng-container>
                    </div>
                </div>
            </div>

            <div class="my-6 border-t border-dashed border-gray-300 dark:border-[#191e3a]"></div>

            <!-- One Time Fee Structure -->
            <div class="space-y-1">
                <h2 class="text-lg font-bold text-black dark:text-white">One Time Fee Structure</h2>
                <p class="text-sm text-gray-500 dark:text-gray-400">Enter and upload details related to your one-time
                    payment.</p>
            </div>

            <div class="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
                <div>
                    <label for="amountReceived" class="mb-1.5 block text-sm font-medium">Amount Received</label>
                    <input id="amountReceived" name="amountReceived" type="text" inputmode="decimal"
                        [(ngModel)]="amountReceived" (ngModelChange)="clearSubmitErrorOnly()"
                        class="form-input w-full rounded-lg" placeholder="00000" />
                </div>
                <div>
                    <label for="dateOfReceipt" class="mb-1.5 block text-sm font-medium">Date of Receipt</label>
                    <div class="relative">
                        <input id="dateOfReceipt" type="text" name="dateOfReceipt" mwlFlatpickr
                            [(ngModel)]="dateOfReceipt" (ngModelChange)="clearFieldFeedback('dateOfReceipt')"
                            [dateFormat]="'d/m/Y'" [convertModelValue]="true" [disableMobile]="true" [minDate]="'today'"
                            placeholder="Select Date of Receipt"
                            class="form-input w-full rounded-lg ltr:pr-10 rtl:pl-10" />
                        <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pe-3">
                            <icon-calendar class="size-5 text-gray-500 dark:text-gray-400" />
                        </div>
                    </div>
                    <p *ngIf="fieldErrors.dateOfReceipt" class="mt-1 text-xs text-red-600 dark:text-red-400"
                        role="alert">
                        {{ fieldErrors.dateOfReceipt }}
                    </p>
                </div>
                <div>
                    <label for="unNumber" class="mb-1.5 block text-sm font-medium">UN Number</label>
                    <input id="unNumber" name="unNumber" type="text" [(ngModel)]="unNumber"
                        (ngModelChange)="clearSubmitErrorOnly()" class="form-input w-full rounded-lg"
                        placeholder="Enter UN Number" />
                </div>
            </div>

            <div class="my-6 border-t border-dashed border-gray-300 dark:border-[#191e3a]"></div>

            <!-- Post Paid -->
            <div class="space-y-1">
                <h2 class="text-lg font-bold text-black dark:text-white">Post Paid</h2>
                <p class="text-sm text-gray-500 dark:text-gray-400">Per-PD charges are set per product type above (under Service &amp; product types).
                </p>
            </div>

            <div class="mt-8 space-y-4 border-t border-dashed border-gray-300 pt-6 dark:border-[#191e3a]">
                <div *ngIf="submitError"
                    class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200"
                    role="alert">
                    {{ submitError }}
                </div>
                <div class="flex flex-wrap justify-end gap-3">
                    <a routerLink="/manage-client" class="btn bg-white text-black rounded-full px-6">Cancel</a>
                    <button type="submit"
                        class="btn rounded-full bg-[#303030] px-8 font-semibold text-white shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
                        [disabled]="submitting">
                        {{ submitting ? 'Saving…' : 'Save' }}
                    </button>
                </div>
            </div>
        </div>
    </form>

    <!-- Success modal: centered overlay, auto-redirect after 2s -->
    <dialog *ngIf="showSuccessModal" open class="fixed inset-0 z-[200] m-0 flex h-full max-h-none w-full max-w-none items-center justify-center border-0 bg-transparent p-4 !mt-0"
        aria-labelledby="create-client-success-title" (keydown.escape)="closeSuccessModal()">
        <div class="absolute inset-0 bg-black/50 backdrop-blur-[1px]" aria-hidden="true"></div>
        <div
            class="relative z-10 w-full max-w-md rounded-3xl bg-white px-8 pb-10 pt-14 text-center shadow-2xl dark:bg-[#0e1726]">
            <button type="button"
                class="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-[#18214d] transition hover:bg-gray-100 dark:text-white-dark dark:hover:bg-white/10"
                (click)="closeSuccessModal()" aria-label="Close">
                <icon-x-circle class="size-5 hover:text-danger" />
            </button>

            <div class="mx-auto mb-6 flex h-[7.5rem] w-[7.5rem] items-center justify-center">
                <svg class="text-emerald-500" width="120" height="120" viewBox="0 0 120 120" fill="none"
                    xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <g transform="translate(60,60)">
                        <line x1="0" y1="-38" x2="0" y2="-50" stroke="currentColor" stroke-width="3.5"
                            stroke-linecap="round" transform="rotate(0)" />
                        <line x1="0" y1="-38" x2="0" y2="-50" stroke="currentColor" stroke-width="3.5"
                            stroke-linecap="round" transform="rotate(45)" />
                        <line x1="0" y1="-38" x2="0" y2="-50" stroke="currentColor" stroke-width="3.5"
                            stroke-linecap="round" transform="rotate(90)" />
                        <line x1="0" y1="-38" x2="0" y2="-50" stroke="currentColor" stroke-width="3.5"
                            stroke-linecap="round" transform="rotate(135)" />
                        <line x1="0" y1="-38" x2="0" y2="-50" stroke="currentColor" stroke-width="3.5"
                            stroke-linecap="round" transform="rotate(180)" />
                        <line x1="0" y1="-38" x2="0" y2="-50" stroke="currentColor" stroke-width="3.5"
                            stroke-linecap="round" transform="rotate(225)" />
                        <line x1="0" y1="-38" x2="0" y2="-50" stroke="currentColor" stroke-width="3.5"
                            stroke-linecap="round" transform="rotate(270)" />
                        <line x1="0" y1="-38" x2="0" y2="-50" stroke="currentColor" stroke-width="3.5"
                            stroke-linecap="round" transform="rotate(315)" />
                    </g>
                    <circle cx="60" cy="60" r="28" fill="#22c55e" />
                    <path d="M48 61 L56 69 L74 49" stroke="white" stroke-width="3.2" fill="none" stroke-linecap="round"
                        stroke-linejoin="round" />
                </svg>
            </div>

            <h2 id="create-client-success-title" class="text-lg font-bold text-black dark:text-white md:text-xl">
                Client created
            </h2>
            <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">The email has been sent to the client for creating
                new password and login to the portal.</p>

            <!-- One-time API key: shown only on creation, never retrievable again. -->
            <div *ngIf="issuedApiKey" class="mt-5 rounded-2xl border border-amber-300/60 bg-amber-50 p-4 text-left dark:border-amber-400/30 dark:bg-amber-400/10">
                <p class="mb-2 text-xs font-semibold text-amber-700 dark:text-amber-300">
                    API key — copy it now. It will not be shown again.
                </p>
                <div class="flex items-center gap-2 rounded-lg bg-white px-3 py-2 dark:bg-[#0e1726]">
                    <code class="flex-1 break-all text-xs font-mono text-black dark:text-white">{{ issuedApiKey.key }}</code>
                    <button type="button" class="shrink-0 text-xs font-medium text-primary underline"
                        (click)="copyApiKey()">
                        {{ apiKeyCopied ? 'Copied!' : 'Copy' }}
                    </button>
                </div>
                <p class="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
                    Environment <strong>{{ issuedApiKey.environment }}</strong> · scopes:
                    <span *ngFor="let s of issuedApiKey.scopes; let last = last"><code class="rounded bg-gray-100 px-1 py-0.5 font-mono dark:bg-white/10">{{ s }}</code>{{ last ? '' : ', ' }}</span>
                </p>
                <p class="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
                    Deliver this securely to the client. Rotate or revoke it anytime from Integrations.
                </p>
                <button type="button"
                    class="mt-4 w-full rounded-full bg-[#303030] px-6 py-2 text-sm text-white"
                    (click)="closeSuccessModal()">
                    Done
                </button>
            </div>
        </div>
</dialog>